import React, { useState, useEffect } from 'react';
import { 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell
} from 'recharts';
import { 
  Users, 
  DollarSign, 
  Clock, 
  CheckCircle
} from 'lucide-react';
import { 
  getNameNumerologyOrders, 
  getCompatibilityOrders
} from '../services/api';
import { 
  NameNumerologyOrder, 
  LoveCompatibilityOrder, 
  DashboardStats 
} from '../types';

const Overview: React.FC = () => {
  const [stats, setStats] = useState<DashboardStats>({
    totalOrders: 0,
    totalRevenue: 0,
    pendingOrders: 0,
    completedOrders: 0,
    nameNumerologyOrders: 0,
    loveCompatibilityOrders: 0
  });
  const [loading, setLoading] = useState(true);
  const [recentOrders, setRecentOrders] = useState<(NameNumerologyOrder | LoveCompatibilityOrder)[]>([]);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const [numerologyOrders, compatibilityOrders] = await Promise.all([
        getNameNumerologyOrders({ limit: 100, offset: 0 }),
        getCompatibilityOrders({ limit: 100, offset: 0 })
      ]);

      // Ensure both responses are arrays
      const numerologyArray = Array.isArray(numerologyOrders) ? numerologyOrders : [];
      const compatibilityArray = Array.isArray(compatibilityOrders) ? compatibilityOrders : [];

      const totalOrders = numerologyArray.length + compatibilityArray.length;
      const totalRevenue = [...numerologyArray, ...compatibilityArray]
        .reduce((sum, order) => sum + parseFloat(order.order_amount), 0);
      
      const pendingOrders = [...numerologyArray, ...compatibilityArray]
        .filter(order => order.status === 'INITIATED' || order.status === 'PENDING').length;
      
      const completedOrders = [...numerologyArray, ...compatibilityArray]
        .filter(order => order.status === 'Completed' || order.status === 'SUCCESS').length;

      setStats({
        totalOrders,
        totalRevenue,
        pendingOrders,
        completedOrders,
        nameNumerologyOrders: numerologyArray.length,
        loveCompatibilityOrders: compatibilityArray.length
      });

      // Get recent orders (last 10)
      const allOrders = [...numerologyArray, ...compatibilityArray]
        .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
        .slice(0, 10);
      
      setRecentOrders(allOrders);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      // Set default values on error
      setStats({
        totalOrders: 0,
        totalRevenue: 0,
        pendingOrders: 0,
        completedOrders: 0,
        nameNumerologyOrders: 0,
        loveCompatibilityOrders: 0
      });
      setRecentOrders([]);
    } finally {
      setLoading(false);
    }
  };

  const chartData = [
    { name: 'Name Numerology', value: stats.nameNumerologyOrders, color: '#ef4444' },
    { name: 'Love Compatibility', value: stats.loveCompatibilityOrders, color: '#0ea5e9' }
  ];

  const statusData = [
    { name: 'Completed', value: stats.completedOrders, color: '#10b981' },
    { name: 'Pending', value: stats.pendingOrders, color: '#f59e0b' }
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="card">
          <div className="flex items-center">
            <div className="p-2 bg-primary-100 rounded-lg">
              <Users className="w-6 h-6 text-primary-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Total Orders</p>
              <p className="text-2xl font-bold text-gray-900">{stats.totalOrders}</p>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="flex items-center">
            <div className="p-2 bg-green-100 rounded-lg">
              <DollarSign className="w-6 h-6 text-green-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Total Revenue</p>
              <p className="text-2xl font-bold text-gray-900">₹{stats.totalRevenue.toFixed(2)}</p>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="flex items-center">
            <div className="p-2 bg-yellow-100 rounded-lg">
              <Clock className="w-6 h-6 text-yellow-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Pending Orders</p>
              <p className="text-2xl font-bold text-gray-900">{stats.pendingOrders}</p>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="flex items-center">
            <div className="p-2 bg-green-100 rounded-lg">
              <CheckCircle className="w-6 h-6 text-green-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Completed Orders</p>
              <p className="text-2xl font-bold text-gray-900">{stats.completedOrders}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="card">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Orders by Product Type</h3>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={chartData}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                outerRadius={80}
                fill="#8884d8"
                dataKey="value"
              >
                {chartData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </div>

        <div className="card">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Orders by Status</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={statusData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis />
              <Tooltip />
              <Bar dataKey="value" fill="#8884d8" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Recent Orders */}
      <div className="card">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Recent Orders</h3>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="table-header">Order ID</th>
                <th className="table-header">Customer</th>
                <th className="table-header">Product</th>
                <th className="table-header">Amount</th>
                <th className="table-header">Status</th>
                <th className="table-header">Date</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {Array.isArray(recentOrders) && recentOrders.map((order) => (
                <tr key={order.order_id} className="hover:bg-gray-50">
                  <td className="table-cell font-mono text-sm">{order.order_id}</td>
                  <td className="table-cell">
                    {'customer_name' in order ? order.customer_name : order.customer1_name}
                  </td>
                  <td className="table-cell">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      order.reading_type === 'compatibility' 
                        ? 'bg-blue-100 text-blue-800' 
                        : 'bg-red-100 text-red-800'
                    }`}>
                      {order.reading_type === 'compatibility' ? 'Love Compatibility' : 'Name Numerology'}
                    </span>
                  </td>
                  <td className="table-cell font-medium">₹{order.order_amount}</td>
                  <td className="table-cell">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      order.status === 'Completed' || order.status === 'SUCCESS'
                        ? 'bg-green-100 text-green-800'
                        : order.status === 'INITIATED' || order.status === 'PENDING'
                        ? 'bg-yellow-100 text-yellow-800'
                        : 'bg-gray-100 text-gray-800'
                    }`}>
                      {order.status}
                    </span>
                  </td>
                  <td className="table-cell text-sm text-gray-500">
                    {new Date(order.created_at).toLocaleDateString()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default Overview;
